<template>
  <!-- Desktop Sidebar -->
  <aside
    class="sidebar-desktop"
    :class="{ 'sidebar-collapsed': adminStore.sidebarCollapsed }"
    :style="{ width: adminStore.sidebarWidth }"
  >
    <!-- Logo Section -->
    <div class="sidebar-header">
      <div v-if="!adminStore.sidebarCollapsed" class="logo-full">
        <div class="logo-icon">
          <span>T</span>
        </div>
        <div class="logo-text">
          <h2>Technomac</h2>
          <p>Admin Panel</p>
        </div>
      </div>
      <button
        @click="adminStore.toggleSidebar()"
        class="toggle-btn"
        :class="{ 'toggle-centered': adminStore.sidebarCollapsed }"
        :title="$t('admin.layout.toggleSidebar')"
      >
        <i class="fas" :class="adminStore.sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <!-- Main Section -->
      <div class="nav-section">
        <span v-if="!adminStore.sidebarCollapsed" class="nav-section-title">{{ $t('admin.sections.main') }}</span>
        
        <NuxtLink
          v-for="item in mainNavItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ 'nav-item-active': isActive(item.path) }"
          :title="item.label"
        >
          <div class="nav-icon">
            <i :class="item.icon"></i>
          </div>
          <span v-if="!adminStore.sidebarCollapsed" class="nav-label">{{ item.label }}</span>
          <span v-if="!adminStore.sidebarCollapsed && item.badge" class="nav-badge">{{ item.badge }}</span>
          <div v-if="adminStore.sidebarCollapsed" class="nav-tooltip">{{ item.label }}</div>
        </NuxtLink>
      </div>

      <!-- Billing Section -->
      <div v-if="showBillingSection" class="nav-section">
        <span v-if="!adminStore.sidebarCollapsed" class="nav-section-title">{{ $t('admin.sections.billing') }}</span>
        
        <NuxtLink
          v-for="item in billingNavItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ 'nav-item-active': isActive(item.path) }"
          :title="item.label"
        >
          <div class="nav-icon">
            <i :class="item.icon"></i>
          </div>
          <span v-if="!adminStore.sidebarCollapsed" class="nav-label">{{ item.label }}</span>
          <div v-if="adminStore.sidebarCollapsed" class="nav-tooltip">{{ item.label }}</div>
        </NuxtLink>
      </div>

      <!-- Communication Section -->
      <div v-if="showCommunicationSection" class="nav-section">
        <span v-if="!adminStore.sidebarCollapsed" class="nav-section-title">{{ $t('admin.sections.communication') }}</span>
        
        <NuxtLink
          v-for="item in communicationNavItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ 'nav-item-active': isActive(item.path) }"
          :title="item.label"
        >
          <div class="nav-icon">
            <i :class="item.icon"></i>
          </div>
          <span v-if="!adminStore.sidebarCollapsed" class="nav-label">{{ item.label }}</span>
          <div v-if="adminStore.sidebarCollapsed" class="nav-tooltip">{{ item.label }}</div>
        </NuxtLink>
      </div>

      <!-- Admin Section (Super Admin only) -->
      <div v-if="showAdminSection" class="nav-section">
        <span v-if="!adminStore.sidebarCollapsed" class="nav-section-title">{{ $t('admin.sections.administration') }}</span>
        
        <NuxtLink
          to="/admin/admins"
          class="nav-item"
          :class="{ 'nav-item-active': isActive('/admin/admins') }"
          :title="$t('admin.nav.adminManagement')"
        >
          <div class="nav-icon">
            <i class="fas fa-user-shield"></i>
          </div>
          <span v-if="!adminStore.sidebarCollapsed" class="nav-label">{{ $t('admin.nav.adminManagement') }}</span>
          <div v-if="adminStore.sidebarCollapsed" class="nav-tooltip">{{ $t('admin.nav.adminManagement') }}</div>
        </NuxtLink>
      </div>
    </nav>

    <!-- User Profile Section -->
    <div class="sidebar-footer">
      <div class="user-profile">
        <div class="user-avatar">
          <ClientOnly>
            <span>{{ adminStore.userDisplayName.charAt(0).toUpperCase() }}</span>
          </ClientOnly>
        </div>
        <div v-if="!adminStore.sidebarCollapsed" class="user-details">
          <ClientOnly>
            <p class="user-name">{{ adminStore.userDisplayName }}</p>
            <p class="user-role">{{ adminStore.user?.role_label || 'Admin' }}</p>
          </ClientOnly>
        </div>
        <div class="status-dot"></div>
      </div>
    </div>
  </aside>

  <!-- Mobile Sidebar -->
  <Transition name="slide">
    <aside v-if="adminStore.sidebarMobileOpen" class="sidebar-mobile">
      <!-- Mobile Header -->
      <div class="sidebar-header">
        <div class="logo-full">
          <div class="logo-icon">
            <span>T</span>
          </div>
          <div class="logo-text">
            <h2>Technomac</h2>
            <p>Admin Panel</p>
          </div>
        </div>
        <button @click="adminStore.closeMobileSidebar()" class="close-btn" :title="$t('admin.layout.closeMenu')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Mobile Navigation -->
      <nav class="sidebar-nav">
        <div class="nav-section">
          <span class="nav-section-title">{{ $t('admin.sections.main') }}</span>
          <NuxtLink
            v-for="item in mainNavItems"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{ 'nav-item-active': isActive(item.path) }"
            @click="adminStore.closeMobileSidebar()"
          >
            <div class="nav-icon"><i :class="item.icon"></i></div>
            <span class="nav-label">{{ item.label }}</span>
            <span v-if="item.badge" class="nav-badge">{{ item.badge }}</span>
          </NuxtLink>
        </div>

        <div v-if="showBillingSection" class="nav-section">
          <span class="nav-section-title">{{ $t('admin.sections.billing') }}</span>
          <NuxtLink
            v-for="item in billingNavItems"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{ 'nav-item-active': isActive(item.path) }"
            @click="adminStore.closeMobileSidebar()"
          >
            <div class="nav-icon"><i :class="item.icon"></i></div>
            <span class="nav-label">{{ item.label }}</span>
          </NuxtLink>
        </div>

        <div v-if="showCommunicationSection" class="nav-section">
          <span class="nav-section-title">{{ $t('admin.sections.communication') }}</span>
          <NuxtLink
            v-for="item in communicationNavItems"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{ 'nav-item-active': isActive(item.path) }"
            @click="adminStore.closeMobileSidebar()"
          >
            <div class="nav-icon"><i :class="item.icon"></i></div>
            <span class="nav-label">{{ item.label }}</span>
          </NuxtLink>
        </div>

        <div v-if="showAdminSection" class="nav-section">
          <span class="nav-section-title">{{ $t('admin.sections.administration') }}</span>
          <NuxtLink
            to="/admin/admins"
            class="nav-item"
            :class="{ 'nav-item-active': isActive('/admin/admins') }"
            @click="adminStore.closeMobileSidebar()"
          >
            <div class="nav-icon"><i class="fas fa-user-shield"></i></div>
            <span class="nav-label">{{ $t('admin.nav.adminManagement') }}</span>
          </NuxtLink>
        </div>
      </nav>

      <!-- Mobile User Profile -->
      <div class="sidebar-footer">
        <div class="user-profile">
          <div class="user-avatar">
            <ClientOnly>
              <span>{{ adminStore.userDisplayName.charAt(0).toUpperCase() }}</span>
            </ClientOnly>
          </div>
          <div class="user-details">
            <ClientOnly>
              <p class="user-name">{{ adminStore.userDisplayName }}</p>
              <p class="user-role">{{ adminStore.user?.role_label || 'Admin' }}</p>
            </ClientOnly>
          </div>
          <div class="status-dot"></div>
        </div>
      </div>
    </aside>
  </Transition>
</template>

<script setup>
const adminStore = useAdminStore()
const route = useRoute()
const { t } = useI18n()

/**
 * Check if user has any of the given permissions
 * Super Admin always has access to everything
 */
const hasAnyPermission = (permissions) => {
  if (adminStore.isSuperAdmin) return true
  if (!adminStore.user?.permissions) return false
  
  const userPermissions = adminStore.user.permissions
  if (userPermissions.includes('*')) return true
  
  return permissions.some(p => userPermissions.includes(p))
}

/**
 * Check if user can access a specific module
 */
const canAccessModule = (module) => {
  return hasAnyPermission([`${module}.view`, `${module}.create`, `${module}.edit`])
}

// Navigation items with permission requirements
const allMainNavItems = [
  { path: '/admin', icon: 'fas fa-home', label: () => t('admin.nav.dashboard'), permissions: ['dashboard.view'] },
  { path: '/admin/appearance', icon: 'fas fa-paint-brush', label: () => 'Appearance', permissions: ['content.view', 'dashboard.view'] },
  { path: '/admin/analytics', icon: 'fas fa-chart-area', label: () => 'Analytiques', permissions: ['dashboard.view'] },
  { path: '/admin/machines', icon: 'fas fa-cog', label: () => t('admin.nav.machines'), permissions: ['machines.view'], badge: () => adminStore.stats?.total_machines || 0 },
  { path: '/admin/brands', icon: 'fas fa-tag', label: () => t('admin.nav.brands'), permissions: ['brands.view'] },
  { path: '/admin/categories', icon: 'fas fa-folder', label: () => t('admin.nav.categories'), permissions: ['categories.view'] },
  { path: '/admin/spare-parts', icon: 'fas fa-wrench', label: () => t('admin.nav.spareParts'), permissions: ['spare-parts.view'] },
  { path: '/admin/services', icon: 'fas fa-truck-loading', label: () => 'Our Work', permissions: ['content.view'], badge: () => adminStore.stats?.total_services || 0 },
  // { path: '/admin/content', icon: 'fas fa-file-alt', label: () => t('admin.nav.content'), permissions: ['content.view'] },
]

const allBillingNavItems = [
  { path: '/admin/billing/clients', icon: 'fas fa-user-tie', label: () => t('admin.nav.clients'), permissions: ['clients.view'] },
  { path: '/admin/billing/invoices', icon: 'fas fa-file-invoice', label: () => t('admin.nav.invoices'), permissions: ['invoices.view'] },
  { path: '/admin/billing/bills', icon: 'fas fa-receipt', label: () => t('admin.nav.bills'), permissions: ['bills.view'] },
  { path: '/admin/billing/proformas', icon: 'fas fa-file-contract', label: () => t('admin.nav.proformas'), permissions: ['proformas.view'] },
  { path: '/admin/billing/dashboard', icon: 'fas fa-chart-line', label: () => t('admin.nav.financialDashboard'), permissions: ['accounting.view', 'statistics.view'] },
  { path: '/admin/stats', icon: 'fas fa-chart-bar', label: () => t('admin.nav.performanceStats'), permissions: ['statistics.view'] },
]

const allCommunicationNavItems = [
  { path: '/admin/contacts', icon: 'fas fa-envelope-open', label: () => t('admin.nav.contacts'), permissions: ['contacts.view'] },
  { path: '/admin/email-campaigns', icon: 'fas fa-envelope', label: () => t('admin.nav.emailCampaigns'), permissions: ['email-campaigns.view'] },
  { path: '/admin/email-subscribers', icon: 'fas fa-users', label: () => t('admin.nav.emailSubscribers'), permissions: ['email-subscribers.view'] },
]

// Filtered navigation items based on permissions
const mainNavItems = computed(() => 
  allMainNavItems.filter(item => hasAnyPermission(item.permissions)).map(item => ({
    ...item,
    label: typeof item.label === 'function' ? item.label() : item.label,
    badge: typeof item.badge === 'function' ? item.badge() : item.badge
  }))
)

const billingNavItems = computed(() => 
  allBillingNavItems.filter(item => hasAnyPermission(item.permissions)).map(item => ({
    ...item,
    label: typeof item.label === 'function' ? item.label() : item.label
  }))
)

const communicationNavItems = computed(() => 
  allCommunicationNavItems.filter(item => hasAnyPermission(item.permissions)).map(item => ({
    ...item,
    label: typeof item.label === 'function' ? item.label() : item.label
  }))
)

// Check if sections should be visible
const showBillingSection = computed(() => billingNavItems.value.length > 0)
const showCommunicationSection = computed(() => communicationNavItems.value.length > 0)
const showAdminSection = computed(() => adminStore.isSuperAdmin)

const isActive = (path) => {
  if (path === '/admin') {
    return route.path === '/admin'
  }
  return route.path === path || route.path.startsWith(path + '/')
}

const handleKeydown = (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
    event.preventDefault()
    adminStore.toggleSidebar()
  }
}

onMounted(async () => {
  adminStore.initializeSidebarState()
  if (!adminStore.stats) {
    try {
      await adminStore.fetchDashboardData()
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    }
  }
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Desktop Sidebar */
.sidebar-desktop {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background: var(--admin-card-bg, #ffffff);
  border-right: 1px solid var(--admin-border-color, #e5e7eb);
  z-index: 40;
  transition: width 0.3s ease;
  flex-direction: column;
}

@media (min-width: 1024px) {
  .sidebar-desktop {
    display: flex;
  }
}

/* Sidebar Header */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid var(--admin-border-color, #e5e7eb);
  min-height: 64px;
}

.logo-full {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--admin-radius-lg, 0.75rem);
  background: var(--admin-gradient-primary, linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.logo-text h2 {
  font-size: 1.125rem;
  font-weight: 700;
  background: var(--admin-gradient-primary, linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  line-height: 1.2;
}

.logo-text p {
  font-size: 0.6875rem;
  color: var(--admin-text-muted, #9ca3af);
  margin: 0;
}

.toggle-btn {
  padding: 0.5rem;
  border: none;
  background: var(--admin-bg-secondary, #f3f4f6);
  color: var(--admin-text-secondary, #6b7280);
  border-radius: var(--admin-radius-md, 0.5rem);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:hover {
  background: var(--admin-primary-100, #e0e7ff);
  color: var(--admin-primary, #6366f1);
}

.toggle-btn i {
  font-size: 0.75rem;
}

.toggle-centered {
  margin: 0 auto;
}

.close-btn {
  padding: 0.5rem;
  border: none;
  background: transparent;
  color: var(--admin-text-muted, #9ca3af);
  border-radius: var(--admin-radius-md, 0.5rem);
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--admin-danger-50, #fef2f2);
  color: var(--admin-danger, #ef4444);
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0.75rem;
}

.nav-section {
  margin-bottom: 1.5rem;
}

.nav-section-title {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--admin-text-muted, #9ca3af);
  padding: 0 0.75rem;
  margin-bottom: 0.5rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: var(--admin-radius-lg, 0.75rem);
  color: var(--admin-text-secondary, #6b7280);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s ease;
  position: relative;
  margin-bottom: 0.25rem;
}

.nav-item:hover {
  background: var(--admin-bg-secondary, #f3f4f6);
  color: var(--admin-text-primary, #111827);
}

.nav-item-active {
  background: var(--admin-gradient-primary, linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%));
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.nav-item-active:hover {
  background: var(--admin-gradient-primary-hover, linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%));
  color: white;
}

.nav-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-icon i {
  font-size: 0.875rem;
}

.nav-item:not(.nav-item-active) .nav-icon {
  color: var(--admin-text-muted, #9ca3af);
}

.nav-item:hover:not(.nav-item-active) .nav-icon {
  color: var(--admin-text-secondary, #6b7280);
}

.nav-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-badge {
  background: var(--admin-primary-100, #e0e7ff);
  color: var(--admin-primary-700, #4338ca);
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: var(--admin-radius-full, 9999px);
}

.nav-item-active .nav-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Tooltip for collapsed state */
.nav-tooltip {
  position: absolute;
  left: calc(100% + 0.75rem);
  top: 50%;
  transform: translateY(-50%);
  background: var(--admin-gray-900, #111827);
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: var(--admin-radius-md, 0.5rem);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.15s ease;
  pointer-events: none;
  z-index: 50;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nav-tooltip::before {
  content: '';
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-right-color: var(--admin-gray-900, #111827);
}

.sidebar-collapsed .nav-item:hover .nav-tooltip {
  opacity: 1;
  visibility: visible;
}

/* Collapsed state adjustments */
.sidebar-collapsed .nav-label,
.sidebar-collapsed .nav-badge,
.sidebar-collapsed .nav-section-title,
.sidebar-collapsed .logo-text,
.sidebar-collapsed .user-details {
  display: none;
}

.sidebar-collapsed .nav-item {
  justify-content: center;
  padding: 0.75rem;
}

.sidebar-collapsed .sidebar-header {
  justify-content: center;
  padding: 1rem 0.5rem;
}

.sidebar-collapsed .user-profile {
  justify-content: center;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--admin-border-color, #e5e7eb);
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--admin-radius-lg, 0.75rem);
  background: var(--admin-gradient-primary, linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--admin-text-primary, #111827);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  color: var(--admin-text-muted, #9ca3af);
  margin: 0;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--admin-success-500, #10b981);
  flex-shrink: 0;
}

/* Mobile Sidebar */
.sidebar-mobile {
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  background: var(--admin-card-bg, #ffffff);
  z-index: 50;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Slide animation */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

/* Hide desktop sidebar on mobile */
@media (max-width: 1023px) {
  .sidebar-desktop {
    display: none;
  }
}
</style>
